import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle as CheckCircleIcon,
  Autorenew as AutorenewIcon,
  LocalPhoneOutlined as LocalPhoneOutlinedIcon,
  Person as PersonIcon,
  SchoolOutlined as SchoolOutlinedIcon,
  EventOutlined as EventOutlinedIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import SignedImage from '../common/SignedImage';
import PageContainer from '../common/PageContainer';

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const religions = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
const statuses = ['ACTIVE', 'SUSPENDED', 'DISCONTINUED', 'TRANSFERRED', 'GRADUATED'];
const transportOptions = ['Bus', 'Private Vehicle', 'Walk', 'Other'];
const genders = ['Male', 'Female', 'Others'];

const Surface = styled(Box)(({ theme }) => ({
  borderRadius: 6,
  border: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
  boxShadow: '0 22px 54px -34px rgba(15,23,42,0.30)',
  background: '#fff',
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.72rem',
  color: alpha(theme.palette.text.primary, 0.75),
  fontWeight: 700,
  marginBottom: theme.spacing(0.6),
  textTransform: 'uppercase',
  letterSpacing: 0.2,
}));

const SectionCard = ({ title, subtitle, children }) => (
  <Surface>
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {subtitle}
        </Typography>
      ) : null}
      {children}
    </Box>
  </Surface>
);

const QuickInfo = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.2} alignItems="flex-start">
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: alpha('#2563eb', 0.10),
        color: '#2563eb',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>
        {value || 'Not added'}
      </Typography>
    </Box>
  </Stack>
);

const CreateStudent = ({ editMode }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const schoolId = localStorage.getItem('schoolId');

  const [studentData, setStudentData] = useState({
    admissionNumber: '',
    admissionDate: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    parentName: '',
    parentPhone: '',
    phone: '',
    classId: '',
    sectionId: '',
    state: '',
    religion: '',
    caste: '',
    fee: '',
    bloodGroup: '',
    transportOption: '',
    busRoute: '',
    address: '',
    email: '',
    password: '',
    status: 'ACTIVE',
    medicalHistory: '',
    hasTransportation: false,
    hasHostel: false,
    createdBy: '',
    updatedBy: '',
    schoolId,
  });

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [busRoutes, setBusRoutes] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [isGeneratingAdmissionNo, setIsGeneratingAdmissionNo] = useState(false);

  const requiredFields = useMemo(
    () => ['admissionDate', 'firstName', 'gender', 'parentName', 'parentPhone', 'status'],
    []
  );

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleError = (error, defaultMessage) => {
    if (error.response) {
      showSnackbar(`Error: ${error.response.data.message || defaultMessage}`, 'error');
    } else if (error.request) {
      showSnackbar('Error: No response from server.', 'error');
    } else {
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axiosInstance.get('/api/classes', { params: { schoolId } });
        setClasses(response.data || []);
      } catch (error) {
        handleError(error, 'Failed to fetch classes.');
      }
    };

    const fetchBusRoutes = async () => {
      try {
        const response = await axiosInstance.get('/api/routes', { params: { schoolId } });
        setBusRoutes(response.data || []);
      } catch (error) {
        handleError(error, 'Failed to fetch bus routes.');
      }
    };

    const fetchStudentData = async () => {
      if (!editMode || !studentId) return;
      try {
        const response = await axiosInstance.get(`/api/students/${studentId}`);
        const data = response.data;
        setStudentData({
          ...data,
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
          admissionDate: data.admissionDate ? data.admissionDate.split('T')[0] : '',
          classId: data.classId || '',
          sectionId: data.sectionId || '',
          gender: data.gender || '',
          status: data.status || 'ACTIVE',
          hasTransportation: !!data.hasTransportation,
          hasHostel: !!data.hasHostel,
          transportOption: data.transportOption || '',
        });
        if (data.classId) {
          const sectionRes = await axiosInstance.get('/api/sections', {
            params: { classId: data.classId, schoolId },
          });
          setSections(sectionRes.data || []);
        }
        if (data.profileImage) setImagePreview(data.profileImage);
      } catch (error) {
        handleError(error, 'Failed to fetch student data.');
      }
    };

    fetchClasses();
    fetchBusRoutes();
    fetchStudentData();
  }, [editMode, studentId, schoolId]);

  useEffect(() => {
    const fetchAdmissionNumberSuggestion = async () => {
      if (editMode || !schoolId) return;
      try {
        setIsGeneratingAdmissionNo(true);
        const response = await axiosInstance.get('/api/students/admission-number/next', {
          headers: { schoolId },
          skipGlobalError: true,
        });
        const suggestion = response.data?.admissionNumber || '';
        setStudentData((prev) => (
          prev.admissionNumber && prev.admissionNumber.trim()
            ? prev
            : { ...prev, admissionNumber: suggestion }
        ));
      } catch (error) {
        handleError(error, 'Failed to generate admission number.');
      } finally {
        setIsGeneratingAdmissionNo(false);
      }
    };

    fetchAdmissionNumberSuggestion();
  }, [editMode, schoolId]);

  useEffect(() => {
    const fetchSections = async () => {
      if (!studentData.classId) {
        setSections([]);
        setStudentData((prev) => ({ ...prev, sectionId: '' }));
        return;
      }
      try {
        const response = await axiosInstance.get('/api/sections', {
          params: { classId: studentData.classId, schoolId },
        });
        setSections(response.data || []);
      } catch (error) {
        handleError(error, 'Failed to fetch sections.');
      }
    };
    fetchSections();
  }, [studentData.classId, schoolId]);

  useEffect(() => {
    const filled = requiredFields.filter((field) => studentData[field] && studentData[field].toString().trim() !== '').length;
    setCompletionPercentage(Math.round((filled / requiredFields.length) * 100));
  }, [studentData, requiredFields]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStudentData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const temp = {};
    temp.firstName = studentData.firstName ? '' : 'First Name is required.';
    temp.parentPhone = /^\d{10}$/.test(studentData.parentPhone || '') ? '' : 'Parent Phone must be 10 digits.';
    temp.admissionNumber = studentData.admissionNumber ? '' : 'Admission No is required.';
    temp.admissionDate = studentData.admissionDate ? '' : 'Admission Date is required.';
    temp.gender = studentData.gender ? '' : 'Gender is required.';
    temp.parentName = studentData.parentName ? '' : 'Parent Name is required.';
    temp.status = studentData.status ? '' : 'Status is required.';
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar('Please fix the errors in the form.', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      const method = editMode ? 'put' : 'post';
      const url = editMode ? `/api/students/${studentId}` : '/api/students';
      const payload = {
        ...studentData,
        hasTransportation: studentData.hasTransportation ? 1 : 0,
        hasHostel: studentData.hasHostel ? 1 : 0,
        updatedBy: 1,
        createdBy: editMode ? studentData.createdBy : 1,
      };
      const response = await axiosInstance[method](url, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      const newStudentId = editMode ? studentId : response.data?.studentId;

      if (imageFile && newStudentId) {
        if (!schoolId) {
          showSnackbar('Student saved but image upload skipped (school not set).', 'warning');
        } else {
          const formData = new FormData();
          formData.append('image', imageFile);
          formData.append('studentId', newStudentId);
          try {
            const uploadResponse = await axiosInstance.post('/api/images/profile/student/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data', schoolId },
            });
            const uploadedUrl = uploadResponse?.data;
            if (uploadedUrl) {
              setStudentData((prev) => ({ ...prev, imageUrl: uploadedUrl }));
              setImagePreview(uploadedUrl);
            }
          } catch {
            showSnackbar('Student saved but image upload failed.', 'warning');
          }
        }
      }

      showSnackbar(`Student ${editMode ? 'updated' : 'created'} successfully.`, 'success');
      navigate(`/students/${newStudentId}/view-student`);
    } catch (error) {
      handleError(error, `Failed to ${editMode ? 'update' : 'create'} student.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = editMode ? 'Edit Student' : 'Add Student';
  const pageSubtitle = editMode
    ? 'Update admission, parent, and academic details in a cleaner student workspace.'
    : 'Create a new student admission record with the information school admins need first.';
  const errorList = Object.values(errors).filter(Boolean);

  return (
    <PageContainer>
      <Box sx={{ maxWidth: 1120, width: '100%', mx: 'auto' }}>
        <Surface sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={editMode ? 'Student Update' : 'New Admission'}
                    sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.10), color: theme.palette.primary.dark }}
                  />
                  <Chip
                    size="small"
                    label={`Completion ${completionPercentage}%`}
                    sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.10), color: theme.palette.success.dark }}
                  />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>
                  {pageTitle}
                </Typography>
                <Typography sx={{ color: 'text.secondary', maxWidth: 760 }}>
                  {pageSubtitle}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 10,
                  borderRadius: 6,
                  backgroundColor: alpha(theme.palette.divider, 0.35),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    backgroundColor:
                      completionPercentage < 50
                        ? theme.palette.error.main
                        : completionPercentage < 80
                          ? theme.palette.warning.main
                          : theme.palette.success.main,
                  },
                }}
              />
              <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: 'text.secondary' }}>
                Required fields completed
              </Typography>
            </Grid>
          </Grid>
        </Surface>

        {errorList.length > 0 && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {errorList.map((err, idx) => <div key={idx}>{err}</div>)}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <SectionCard title="Student Details" subtitle="Basic student identity and contact details.">
                  <Grid container spacing={1.4}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>First Name</FieldLabel>
                      <TextField size="small" name="firstName" value={studentData.firstName} onChange={handleChange} required fullWidth error={!!errors.firstName} helperText={errors.firstName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Last Name</FieldLabel>
                      <TextField size="small" name="lastName" value={studentData.lastName} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Date of Birth</FieldLabel>
                      <TextField size="small" name="dateOfBirth" type="date" value={studentData.dateOfBirth} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Gender</FieldLabel>
                      <FormControl fullWidth required error={!!errors.gender}>
                        <Select size="small" name="gender" value={studentData.gender} onChange={handleChange} displayEmpty>
                          <MenuItem value=""><em>Select gender</em></MenuItem>
                          {genders.map((gender) => <MenuItem key={gender} value={gender}>{gender}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Student Phone</FieldLabel>
                      <TextField size="small" name="phone" type="tel" value={studentData.phone} onChange={handleChange} fullWidth inputProps={{ maxLength: 10, pattern: '[0-9]*' }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Email</FieldLabel>
                      <TextField size="small" name="email" type="email" value={studentData.email} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <FieldLabel>Address</FieldLabel>
                      <TextField size="small" name="address" value={studentData.address} onChange={handleChange} fullWidth multiline rows={2} />
                    </Grid>
                  </Grid>
                </SectionCard>

                <SectionCard title="Parent / Guardian" subtitle="Parent details used for communication and login-related flows.">
                  <Grid container spacing={1.4}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Parent Name</FieldLabel>
                      <TextField size="small" name="parentName" value={studentData.parentName} onChange={handleChange} required fullWidth error={!!errors.parentName} helperText={errors.parentName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Parent Phone</FieldLabel>
                      <TextField size="small" name="parentPhone" value={studentData.parentPhone} onChange={handleChange} type="tel" required fullWidth error={!!errors.parentPhone} helperText={errors.parentPhone} inputProps={{ maxLength: 10, pattern: '[0-9]*' }} />
                    </Grid>
                  </Grid>
                </SectionCard>

                <SectionCard title="Admission & Academic" subtitle="Class placement, admission date, and operational setup.">
                  <Grid container spacing={1.4}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Admission No</FieldLabel>
                      <TextField
                        size="small"
                        name="admissionNumber"
                        value={studentData.admissionNumber}
                        onChange={handleChange}
                        required
                        fullWidth
                        error={!!errors.admissionNumber}
                        helperText={errors.admissionNumber || 'Permanent student ID. Format: ORGCODE-2026-00001. Admin can edit.'}
                        InputProps={{
                          endAdornment: !editMode ? (
                            <Button
                              size="small"
                              onClick={async () => {
                                try {
                                  setIsGeneratingAdmissionNo(true);
                                  const response = await axiosInstance.get('/api/students/admission-number/next', {
                                    headers: { schoolId },
                                    skipGlobalError: true,
                                  });
                                  setStudentData((prev) => ({ ...prev, admissionNumber: response.data?.admissionNumber || prev.admissionNumber }));
                                } catch (error) {
                                  handleError(error, 'Failed to generate admission number.');
                                } finally {
                                  setIsGeneratingAdmissionNo(false);
                                }
                              }}
                              startIcon={isGeneratingAdmissionNo ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon sx={{ fontSize: 16 }} />}
                              disabled={isGeneratingAdmissionNo}
                              sx={{ minWidth: 'auto', textTransform: 'none' }}
                            >
                              Generate
                            </Button>
                          ) : null,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Admission Date</FieldLabel>
                      <TextField size="small" name="admissionDate" type="date" value={studentData.admissionDate} onChange={handleChange} required fullWidth InputLabelProps={{ shrink: true }} error={!!errors.admissionDate} helperText={errors.admissionDate} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Status</FieldLabel>
                      <FormControl fullWidth required error={!!errors.status}>
                        <Select size="small" name="status" value={studentData.status} onChange={handleChange}>
                          {statuses.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Class</FieldLabel>
                      <FormControl fullWidth>
                        <Select size="small" name="classId" value={studentData.classId} onChange={handleChange} displayEmpty>
                          <MenuItem value=""><em>Select class</em></MenuItem>
                          {classes.map((cls) => <MenuItem key={cls.classId} value={cls.classId}>{cls.className}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Section</FieldLabel>
                      <FormControl fullWidth disabled={!studentData.classId}>
                        <Select size="small" name="sectionId" value={studentData.sectionId} onChange={handleChange} displayEmpty>
                          <MenuItem value=""><em>Select section</em></MenuItem>
                          {sections.map((section) => <MenuItem key={section.sectionId} value={section.sectionId}>{section.sectionName}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Fee</FieldLabel>
                      <TextField size="small" name="fee" type="number" value={studentData.fee} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Blood Group</FieldLabel>
                      <FormControl fullWidth>
                        <Select size="small" name="bloodGroup" value={studentData.bloodGroup} onChange={handleChange} displayEmpty>
                          <MenuItem value=""><em>Select blood group</em></MenuItem>
                          {bloodGroups.map((group) => <MenuItem key={group} value={group}>{group}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Religion</FieldLabel>
                      <FormControl fullWidth>
                        <Select size="small" name="religion" value={studentData.religion} onChange={handleChange} displayEmpty>
                          <MenuItem value=""><em>Select religion</em></MenuItem>
                          {religions.map((religion) => <MenuItem key={religion} value={religion}>{religion}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Caste</FieldLabel>
                      <TextField size="small" name="caste" value={studentData.caste} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>State</FieldLabel>
                      <TextField size="small" name="state" value={studentData.state} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <FieldLabel>Medical History</FieldLabel>
                      <TextField size="small" name="medicalHistory" value={studentData.medicalHistory} onChange={handleChange} fullWidth multiline rows={2} />
                    </Grid>
                  </Grid>
                </SectionCard>

                <SectionCard title="Transport & Hostel" subtitle="Optional operational services for the student.">
                  <Grid container spacing={1.4}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Transport Option</FieldLabel>
                      <FormControl fullWidth>
                        <Select size="small" name="transportOption" value={studentData.transportOption} onChange={handleChange} displayEmpty>
                          <MenuItem value=""><em>Select transport option</em></MenuItem>
                          {transportOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    {studentData.transportOption === 'Bus' && (
                      <Grid item xs={12} sm={6}>
                        <FieldLabel>Bus Route</FieldLabel>
                        <FormControl fullWidth>
                          <Select size="small" name="busRoute" value={studentData.busRoute} onChange={handleChange} displayEmpty>
                            <MenuItem value=""><em>Select bus route</em></MenuItem>
                            {busRoutes.map((route) => <MenuItem key={route.routeId} value={route.routeName}>{route.routeName}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={<Checkbox checked={studentData.hasTransportation} onChange={handleChange} name="hasTransportation" />}
                        label="Has Transportation"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={<Checkbox checked={studentData.hasHostel} onChange={handleChange} name="hasHostel" />}
                        label="Has Hostel"
                      />
                    </Grid>
                  </Grid>
                </SectionCard>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <SectionCard title="Photo" subtitle="Student profile image used across reports and profile screens.">
                  <Box
                    sx={{
                      width: 180,
                      maxWidth: '100%',
                      aspectRatio: '35 / 45',
                      borderRadius: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                      backgroundColor: '#f8fafc',
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                      mb: 1.5,
                      mx: 'auto',
                    }}
                  >
                    {imagePreview ? (
                      <SignedImage src={imagePreview} alt="Profile Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <PersonIcon sx={{ fontSize: 88, color: alpha(theme.palette.text.primary, 0.34) }} />
                    )}
                  </Box>
                  <input accept="image/*" style={{ display: 'none' }} id="upload-profile-image" type="file" onChange={handleImageChange} />
                  <label htmlFor="upload-profile-image">
                    <Button variant="outlined" component="span" fullWidth sx={{ textTransform: 'none', borderRadius: 3 }}>
                      Upload Image
                    </Button>
                  </label>
                </SectionCard>

                <SectionCard title="Quick Summary" subtitle="Helpful context while filling the admission form.">
                  <Stack spacing={1.5}>
                    <QuickInfo icon={<SchoolOutlinedIcon sx={{ fontSize: 18 }} />} label="Admission No" value={studentData.admissionNumber} />
                    <QuickInfo icon={<SchoolOutlinedIcon sx={{ fontSize: 18 }} />} label="Class / Section" value={[classes.find((c) => String(c.classId) === String(studentData.classId))?.className, sections.find((s) => String(s.sectionId) === String(studentData.sectionId))?.sectionName].filter(Boolean).join(' / ')} />
                    <QuickInfo icon={<LocalPhoneOutlinedIcon sx={{ fontSize: 18 }} />} label="Parent Phone" value={studentData.parentPhone} />
                    <QuickInfo icon={<EventOutlinedIcon sx={{ fontSize: 18 }} />} label="Admission Date" value={studentData.admissionDate} />
                    <QuickInfo icon={<LocalPhoneOutlinedIcon sx={{ fontSize: 18 }} />} label="Student Phone" value={studentData.phone} />
                  </Stack>
                </SectionCard>

                <SectionCard title="Actions" subtitle="Save the admission or return to the student list.">
                  <Stack spacing={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                      disabled={isSubmitting}
                      sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 700, boxShadow: 'none' }}
                    >
                      {isSubmitting ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update Student' : 'Save Student')}
                    </Button>
                    <Button variant="outlined" color="inherit" onClick={() => navigate('/students/list')} disabled={isSubmitting} sx={{ textTransform: 'none', borderRadius: 3 }}>
                      Cancel
                    </Button>
                  </Stack>
                </SectionCard>
              </Stack>
            </Grid>
          </Grid>
        </form>

        <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
          <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </PageContainer>
  );
};

export default CreateStudent;
